/**
 * HM-413 cluster 17.2 polish: modal "Modifica prenotazione" aperto dal
 * bottone Modifica del BookingDetailDrawer. Form ricco con dropdown
 * camera + datetime-local per nuovo start/end. Riusa PATCH /bookings/:id/reschedule
 * (HM-392) sotto. Validazione client: end > start; submit disabled se invariato.
 */
import { useMemo, useState } from 'react';
import type { PlanningItem } from '../../api/planning.js';
import type { RoomDb } from '../../api/rooms.js';

function isoToLocalInput(iso: string): string {
  // Converte ISO -> "YYYY-MM-DDTHH:mm" per <input type=datetime-local>.
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

function localInputToIso(local: string): string {
  // datetime-local non porta timezone: lo interpretiamo come locale del browser.
  return new Date(local).toISOString();
}

export function RescheduleBookingModal({
  booking,
  rooms,
  pending,
  onSubmit,
  onCancel,
}: {
  booking: PlanningItem;
  rooms: RoomDb[];
  pending: boolean;
  onSubmit: (input: { newRoomId: string; newStartAt: string; newEndAt: string }) => void;
  onCancel: () => void;
}) {
  const initial = useMemo(
    () => ({
      newRoomId: booking.roomId ?? rooms[0]?.id ?? '',
      newStartLocal: isoToLocalInput(booking.startAt),
      newEndLocal: isoToLocalInput(booking.endAt),
    }),
    [booking, rooms],
  );

  const [newRoomId, setNewRoomId] = useState(initial.newRoomId);
  const [newStartLocal, setNewStartLocal] = useState(initial.newStartLocal);
  const [newEndLocal, setNewEndLocal] = useState(initial.newEndLocal);

  const endAfterStart = useMemo(() => {
    const s = new Date(newStartLocal).getTime();
    const e = new Date(newEndLocal).getTime();
    return Number.isFinite(s) && Number.isFinite(e) && e > s;
  }, [newStartLocal, newEndLocal]);

  const isChanged =
    newRoomId !== initial.newRoomId ||
    newStartLocal !== initial.newStartLocal ||
    newEndLocal !== initial.newEndLocal;

  const canSubmit = endAfterStart && isChanged && !pending;

  return (
    <div
      className="planning-modal-overlay"
      // biome-ignore lint/a11y/useSemanticElements: pattern overlay div coerente con RescheduleConfirmModal/BookingDetailDrawer.
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onCancel();
      }}
      data-testid="reschedule-modal"
    >
      <div className="planning-modal">
        <div className="planning-modal-title">Sposta prenotazione</div>
        <div className="planning-modal-body">
          <label
            htmlFor="reschedule-room"
            style={{ display: 'block', fontSize: 12, color: '#7A6957', marginBottom: 4 }}
          >
            Camera
          </label>
          <select
            id="reschedule-room"
            value={newRoomId}
            onChange={(e) => setNewRoomId(e.target.value)}
            data-testid="reschedule-room-select"
            style={{
              width: '100%',
              padding: '8px 10px',
              marginBottom: 12,
              borderRadius: 6,
              border: '1px solid #D4C4B0',
            }}
            disabled={pending}
          >
            {rooms.map((r) => (
              <option key={r.id} value={r.id}>
                {r.code} — Piano {r.floor}
              </option>
            ))}
          </select>

          <label
            htmlFor="reschedule-start"
            style={{ display: 'block', fontSize: 12, color: '#7A6957', marginBottom: 4 }}
          >
            Nuovo check-in
          </label>
          <input
            id="reschedule-start"
            type="datetime-local"
            value={newStartLocal}
            onChange={(e) => setNewStartLocal(e.target.value)}
            data-testid="reschedule-start-input"
            style={{
              width: '100%',
              padding: '8px 10px',
              marginBottom: 12,
              borderRadius: 6,
              border: '1px solid #D4C4B0',
            }}
            disabled={pending}
          />

          <label
            htmlFor="reschedule-end"
            style={{ display: 'block', fontSize: 12, color: '#7A6957', marginBottom: 4 }}
          >
            Nuovo check-out
          </label>
          <input
            id="reschedule-end"
            type="datetime-local"
            value={newEndLocal}
            onChange={(e) => setNewEndLocal(e.target.value)}
            data-testid="reschedule-end-input"
            style={{
              width: '100%',
              padding: '8px 10px',
              borderRadius: 6,
              border: '1px solid #D4C4B0',
            }}
            disabled={pending}
          />

          {!endAfterStart && (
            <p
              style={{ marginTop: 8, fontSize: 12, color: '#B85450' }}
              data-testid="reschedule-error-range"
            >
              Il check-out deve essere successivo al check-in.
            </p>
          )}
        </div>
        <div className="planning-modal-actions">
          <button
            type="button"
            className="planning-drawer-btn"
            onClick={onCancel}
            disabled={pending}
            data-testid="reschedule-modal-cancel"
          >
            Annulla
          </button>
          <button
            type="button"
            className="planning-drawer-btn primary"
            onClick={() =>
              onSubmit({
                newRoomId,
                newStartAt: localInputToIso(newStartLocal),
                newEndAt: localInputToIso(newEndLocal),
              })
            }
            disabled={!canSubmit}
            data-testid="reschedule-modal-submit"
          >
            {pending ? 'Sposto…' : 'Sposta prenotazione'}
          </button>
        </div>
      </div>
    </div>
  );
}
